<template>
    <!-- <h1>Pippo</h1> -->

    <div v-show="deviceDataAvailableInReport" class="px-2 text-center">
        <div class="text-md bold">{{ getCurrentDeviceCompleteName }}: Global market share</div>
    </div>
    <div v-show="deviceDataAvailableInReport" id="map" class="text-center"></div>
    <div v-show="deviceDataAvailableInReport" class="text-sm italics">Note: This choropleth is provided for illustrative purposes only, the actual device market share of '{{ getCurrentDeviceCompleteName }}' was measured in Q1 2023 and may no longer reflect current market share.</div>

    <div v-show="!deviceDataAvailableInReport" class="text-sm italics">This mobile device did not have a significant market share when measured in Q1 2023. For this reason, no infographics was produced for this device.</div>
</template>

<script>
import {useUiElements} from "./../store/uiElements"
import {mapActions, mapState} from 'pinia'
import * as d3 from "d3";
import * as topojson from "topojson";


export default {
    name: "MapComponent",

    data() {
        return {
            deviceDataAvailableInReport: false
        }
    },

    computed: {
        ...mapState(useUiElements, {
            selectedDevice: "selectedDevice",
            getCurrentDeviceCompleteName: 'getCurrentDeviceCompleteName'
        })
    },

    methods: {

        // ...mapActions(useUiElements, [
        //     'getCurrentDeviceCompleteName'
        // ]),

        clearMap() {

            // Clear existing paths
            d3.select(".tooltip").remove();
            d3.select("#map").selectAll("*").remove();
        },

        generateMap() {

            const width = 960;
            const height = 500;

            const svg = d3
                .select("#map")
                .append("svg")
                .attr("viewBox", `0 0 ${width} ${height}`)
                .attr("preserveAspectRatio", "xMidYMid meet");

            // console.log(svg.node());


            const projection = d3
                .geoEquirectangular()
                .scale(170)
                .translate([width / 2, height / 2]);

            const path = d3.geoPath().projection(projection);

            const getColor = (percentage) => {
                        if (percentage === undefined) return "rgba(0, 0, 0, 0)"; // Transparent color for "Seven seas (open ocean)"
                        // console.log(percentage)
                        if (percentage <= 0.001) return "#F3E5F5";
                        if (percentage <= 0.002) return "#EDE7F6";
                        if (percentage <= 0.004) return "#E1BEE7";
                        if (percentage <= 0.006) return "#D1C4E9";
                        if (percentage <= 0.008) return "#C5CAE9";
                        if (percentage <= 0.01) return "#B39DDB";
                        if (percentage <= 0.015) return "#9FA5CF";
                        if (percentage <= 0.02) return "#9C27B0";
                        if (percentage <= 0.025) return "#8E24AA";
                        if (percentage <= 0.03) return "#7B1FA2";
                        if (percentage <= 0.035) return "#6A1B9A";
                        if (percentage <= 0.04) return "#4A148C";
                        if (percentage <= 0.045) return "#311B92";
                        if (percentage <= 0.05) return "#1A237E";
                        if (percentage <= 0.055) return "#1A1C5E";
                        if (percentage <= 0.06) return "#1A1344";
                        if (percentage <= 0.065) return "#150A2A";
                        if (percentage <= 0.07) return "#12005E";
                        if (percentage <= 0.075) return "#0D004C";
                        if (percentage <= 0.08) return "#08003A";
                        if (percentage <= 0.085) return "#030028";
                        if (percentage <= 0.09) return "#000016";
                        if (percentage <= 0.1) return "#20005A";
                        if (percentage <= 0.2) return "#3F006E";
                        if (percentage <= 0.3) return "#5E0082";
                        if (percentage <= 0.4) return "#7D0096";
                        if (percentage <= 0.5) return "#9C00AA";
                        if (percentage <= 0.6) return "#BB00BE";
                        if (percentage <= 0.7) return "#DA00D2";
                        if (percentage <= 0.8) return "#F900E6";
                        if (percentage <= 0.9) return "#FF3EFA";
                        if (percentage <= 1) return "#FF7CFF";

                        return "#12005E";

            };

            const findPercentageByCompleteName = (data, fullName) => {
                const record = data.find(r => r.complete_device_name.toLowerCase() === fullName.toLowerCase());
                if (record) {
                    // console.log("Found record: ", record)
                    this.deviceDataAvailableInReport = true
                    return [
                        parseFloat(record.north_america),
                        parseFloat(record.south_central_america),
                        parseFloat(record.europe),
                        parseFloat(record.asia),
                        parseFloat(record.africa),
                        parseFloat(record.australia)
                    ];
                } else {
                    // console.log("Found no record for device: ", fullName)
                    return [0,0,0,0,0,0]
                }
            }

            const visualizeData = (data, fullName) => {

                const percentages = findPercentageByCompleteName(data, fullName);
                // console.log(percentages)
                // percentages all zero => we have no data for that device. Clean up and get the heck out
                if (percentages.every(item => item === 0)){
                    this.clearMap()
                    this.deviceDataAvailableInReport = false
                    return
                } else {
                    this.deviceDataAvailableInReport = true
                }

                d3.json("/continents.geo.json").then(function (continentsGeoJson) {

                    const features = continentsGeoJson.features;

                    // // Clear existing paths
                    // svg.selectAll(".continent").remove();

                    const continents = ["North America","South America","Europe","Asia","Africa","Australia"];

                    const percentageByContinent = {};
                    continents.forEach((continent, i) => {percentageByContinent[continent] = percentages[i];});

                    const continentPaths = svg
                        .selectAll(".continent")
                        .data(features)
                        .enter()
                        .append("path")
                        .attr("class", "continent")
                        .attr("d", path)
                        .attr("fill", (d) => getColor(percentageByContinent[d.properties.CONTINENT]));

                    continentPaths
                        .on("mouseover", function (event, d) {
                            const continent = d.properties.CONTINENT;
                            const percentage = percentageByContinent[continent];
                            const message = `${continent}: ${(percentage || 0).toFixed(3)}%`;

                            const tooltip = d3
                                .select("body")
                                .append("div")
                                .attr("class", "tooltip")
                                .style("position", "absolute")
                                .style("background-color", "white")
                                .style("border", "1px solid black")
                                .style("padding", "5px")
                                .style("left", event.pageX + 10 + "px")
                                .style("top", event.pageY - 10 + "px")
                                .text(message);
                        })
                        .on("mouseout", function () {
                            d3.select(".tooltip").remove();
                        });
                    });
            }

            const  loadData = (wid) => {
                d3.csv("/data.csv").then(function (data) {
                    visualizeData(data, wid);
                    // updateSize();
                });
            }

            // const updateSize = () => {
            //     const chartContainer = document.getElementById("map");
            //     const containerWidth = chartContainer.clientWidth;
            //     const containerHeight = chartContainer.clientHeight;
            //     console.log("chartContainer:", chartContainer)
            //     const aspectRatio = width / height;

            //     let newWidth = containerWidth;
            //     let newHeight = containerWidth / aspectRatio;

            //     if (newHeight > containerHeight) {
            //     newHeight = containerHeight;
            //     newWidth = containerHeight * aspectRatio;
            //     }S

            //     svg
            //     .attr("width", newWidth)
            //     .attr("height", newHeight);
            // }

            // window.addEventListener("resize", updateSize);

            if (this.selectedDevice) {
                //if there's a selected device
                // loadData(this.selectedDevice.wurfl_id);
                // console.log(this.getCurrentDeviceCompleteName)
                loadData(this.getCurrentDeviceCompleteName);
            }

        }, // end of generate map
    },

    // we watch selectedDevice. When it's set, we reset pagination to page 1
    watch: {
        selectedDevice: function(newVal, oldVal) { // watch it
            console.log('old: ' +oldVal.wurfl_id+ ' ==> New: ' +newVal.wurfl_id)
            this.clearMap()

            // regenerate cloropeth if available
            // this.generateMap(newVal.wurfl_id)
            this.generateMap()
            // this.deviceDataAvailableInReport = true
        }
    }
};
</script>

<style>
    #map {
        max-width: 100%;
        height: auto;
    }

    path {
        stroke: #333;
        stroke-width: 1;
    }
    .bar {
        fill: steelblue;
    }
    .bar:hover {
        fill: brown;
    }
    .axis {
        font: 10px sans-serif;
    }

</style>
